import { useCallback, useEffect, useRef, useState } from "react";

import { getCursorList } from "@/providers/dataProvider/dataProvider";

import { ExtractedRecord } from "@/types";

import { groupErroredRecords, type ErroredRecordGroup, type IndexedErroredRecord } from "../utils/erroredRecordGroups";
import { isRecordIndexUnavailable } from "../utils/exclusionCascade";
import { IndexedExtractedRecord, stampAbsoluteIndicesFromWindow } from "../utils/recordIndexing";

/**
 * The document's errored records, fetched via `?errored=true` (#6853).
 *
 * ## Why this is a separate fetch and not a filter over loaded records
 *
 * A client-side filter filters the loaded PAGE. On a paged document the errors
 * that block approval are, by definition, the ones the reviewer has not seen —
 * measured 2026-09-09 on an 8,000-record fixture, errors sat at indices 50,
 * 5500 and 6700, and ten "Load more" clicks did not reach the second. At
 * 497,711 records that is ~498 clicks. Filtering what is already loaded finds
 * the one error the reviewer could already see and reports it as all of them,
 * which is the same silent-partial-answer class as the search box (#6899).
 *
 * ## The count is whole-document even when the reasons are not
 *
 * `total` comes from the endpoint, so it is the document's errored-record count
 * regardless of how many pages we fetch. The GROUPS are built from the records
 * actually fetched, so on a document with more errored records than one page
 * they describe a prefix. `reasonsArePartial` says so, and the surface must
 * disclose it rather than implying the reason breakdown is complete.
 *
 * That split is deliberate: the count is what gates approval and must be exact,
 * while the reasons are what suggest an action and are useful from a sample.
 * Conflating them would either understate the block or fetch every errored
 * record to name a reason we already know from the first ten.
 */
export interface ErroredRecordsState {
  /** Whole-document count of errored records, from the endpoint's total. */
  total: number;
  /** Reason groups, largest first. Built from the records fetched. */
  groups: ErroredRecordGroup[];
  /**
   * True when `groups` was built from fewer records than `total` — the reason
   * breakdown is a prefix, not the whole picture. The surface must say so.
   *
   * Driven off the page's own `hasMore`, NOT off
   * `sampledRecordCount < total` (!4682 review). The endpoint's total and the
   * page length are two separate server answers, so a length comparison
   * infers truncation where `hasMore` states it — and it infers wrong in the
   * one direction that matters: if the two ever disagree, a length comparison
   * reports "complete" for a page the server said was partial.
   */
  reasonsArePartial: boolean;
  /**
   * True when the server had MORE errored records than this fetch returned, so
   * `records` and `recordIndices` on every group are a PREFIX of the errored
   * set.
   *
   * Load-bearing for the exclude action, not just for copy (!4682 review).
   * Excluding a group applies to the indices in hand, so on a truncated sample
   * "Exclude" resolves at most ERRORED_RECORDS_SAMPLE_SIZE records and the
   * rest still block. A button reading "Exclude 1,000" under a heading saying
   * 5,000 block is a wrong count under a destructive action on claims data,
   * which is why this is separate from `reasonsArePartial`: that one says the
   * REASONS may be incomplete, this one says the ACTION is.
   */
  isTruncated: boolean;
  /** How many errored records the groups were actually built from. */
  sampledRecordCount: number;
  /**
   * The errored records themselves, with absolute `record_index` stamped on.
   *
   * EXPOSED BECAUSE THE GRID NEEDS THEM, not for convenience. The banner
   * navigates to records at indices like 5500 and 6700 while the grid holds
   * 0-999, so filtering the LOADED set by those indices renders an empty grid
   * — which is the stepper's failure mode with a different cause. The grid has
   * to display these records, not look for them among the ones it has.
   */
  records: ExtractedRecord[];
  isLoading: boolean;
  /**
   * A TRANSIENT failure. Excludes the 409 — see `unavailable`.
   *
   * The split matters because the surface names a remedy. Lumping them
   * together rendered "retry before approving" for a 409, which is advice
   * that can never succeed: the derived columns are absent until the document
   * is reprocessed (!4682 review follow-up, found while verifying #6893 AC7).
   */
  error: Error | null;
  /**
   * True when the endpoint answered **409 DOC-000001** — this transformation's
   * record index is absent or was written under a superseded identity
   * definition, so the question cannot be answered for this document at all.
   *
   * The same user-facing situation as the client-side skip
   * (`derivedColumnsAvailable` false): the document cannot be checked and the
   * remedy is a reprocess. The two paths differ only in WHO noticed — the SPA
   * declining to ask, or the server refusing — so the surface converges them
   * on one message rather than growing a fifth state for a distinction the
   * reviewer cannot act on differently.
   */
  unavailable: boolean;
  /** Re-run the fetch, e.g. after a reprocess changes the record set. */
  refresh: () => void;
}

interface UseErroredRecordsProps {
  documentId?: string;
  transformationId?: string;
  /**
   * REQUIRED. Whether migration 000220's derived columns are populated for
   * this transformation.
   *
   * The endpoint answers `?errored=true` with a **409** when they are not, and
   * that is correct — on a pre-000220 transformation `has_validation_errors` is
   * NULL on every row, so an empty page would read as "no errored records"
   * about a document nobody examined.
   *
   * Required rather than optional because a caller who forgets it would fire a
   * request guaranteed to 409 on every historical document, and the error state
   * would look like a server fault. That is the #6848 optional-prop lesson.
   */
  derivedColumnsAvailable: boolean;
  /** Skip entirely when the surface has no use for it. */
  enabled?: boolean;
}

/**
 * How many errored records to fetch for the reason breakdown.
 *
 * Not the whole set, deliberately. The reasons are a closed set of six rules
 * and a real document trips one or two, so a sample characterises the document
 * as well as the full set would — and #6853's own open question ("3 errors or
 * 300?") was settled by observing that reason COUNT does not scale with row
 * count. Fetching 500k errored records to discover two reasons would be the
 * #6826 mistake with a different justification.
 *
 * Equal to the endpoint's max page size, so this is exactly one request.
 */
export const ERRORED_RECORDS_SAMPLE_SIZE = 1000;

export const useErroredRecords = ({
  documentId,
  transformationId,
  derivedColumnsAvailable,
  enabled = true,
}: UseErroredRecordsProps): ErroredRecordsState => {
  const [total, setTotal] = useState(0);
  const [groups, setGroups] = useState<ErroredRecordGroup[]>([]);
  const [records, setRecords] = useState<ExtractedRecord[]>([]);
  const [sampledRecordCount, setSampledRecordCount] = useState(0);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  // Same superseding-generation guard as useTransformationRecords, and for the
  // same reason: a reprocess can swap transformationId while a request is in
  // flight, and the old transformation's record_index values landing under the
  // new one would give the reviewer plausible numbers for the wrong rows.
  const generation = useRef(0);

  const ready = enabled && !!documentId && !!transformationId && derivedColumnsAvailable;

  const fetch = useCallback(async () => {
    if (!ready) {
      // Reset rather than leaving the previous document's numbers on screen.
      //
      // The generation bump comes FIRST. It used to happen only on the fetch
      // path below, so a response already in flight would land after this
      // reset and repopulate the state it had just cleared — the previous
      // document's errored records, under the new one (!4682 review).
      generation.current++;
      setTotal(0);
      setGroups([]);
      setRecords([]);
      setSampledRecordCount(0);
      setIsTruncated(false);
      setError(null);
      setUnavailable(false);
      return;
    }
    const gen = ++generation.current;
    setIsLoading(true);
    setError(null);
    setUnavailable(false);

    try {
      const page = await getCursorList<IndexedExtractedRecord>(
        `documents/${documentId}/document-transformations/${transformationId}/records`,
        { from: 0, limit: ERRORED_RECORDS_SAMPLE_SIZE, filter: { errored: true } }
      );
      if (gen !== generation.current) return;

      const indexed: IndexedErroredRecord[] = page.data.map((r) => ({
        // The server's absolute record_index, which is what an exclusion is
        // expressed in (#6755). NOT array position — on a filtered read the
        // positions are 0..n while the indices are 50, 5500, 6700.
        recordIndex: r.record_index,
        record: r.record,
      }));

      setTotal(page.total);
      setSampledRecordCount(indexed.length);
      setIsTruncated(page.hasMore);
      setGroups(groupErroredRecords(indexed));
      // stampAbsoluteIndicesFromWindow is the ONLY stamper (recordIndexing) —
      // reusing it keeps one definition of "where did this index come from",
      // rather than a second spread that happens to look the same.
      setRecords(stampAbsoluteIndicesFromWindow(page.data));
    } catch (e) {
      if (gen !== generation.current) return;
      // 409 is NOT a transient failure. It means the derived columns are
      // absent or stamped under a superseded identity version, which no
      // number of retries changes — the remedy is a reprocess. Routing it to
      // `error` made the banner say "retry before approving", which is advice
      // that can never succeed.
      if (isRecordIndexUnavailable(e)) {
        setUnavailable(true);
      } else {
        setError(e instanceof Error ? e : new Error(String(e)));
      }
      // Counts are NOT preserved across a failure. A stale "3 records have
      // errors" beside an error state reads as a current fact.
      setTotal(0);
      setGroups([]);
      setRecords([]);
      setSampledRecordCount(0);
      setIsTruncated(false);
    } finally {
      if (gen === generation.current) setIsLoading(false);
    }
  }, [ready, documentId, transformationId]);

  useEffect(() => {
    void fetch();
  }, [fetch]);

  return {
    total,
    groups,
    records,
    reasonsArePartial: isTruncated,
    isTruncated,
    unavailable,
    sampledRecordCount,
    isLoading,
    error,
    refresh: () => void fetch(),
  };
};
